import React, { useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import ColorizeIcon from "@mui/icons-material/Colorize";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { SectionShell } from "../../common/dashboard/SectionShell";
import { useCmpOrganizationNoun } from "../../../contexts/CmpOrganizationNounContext";
import { emailImageAccept, getEmailImageFileError } from "./emailAssetUpload";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
} from "../../../utils/userFacingError";
import {
  EmailBuilderState,
  EmailFooterProfile,
  UpdateEmailBuilderState,
  getReadableEmailTextColor,
  getEmailGreetingText,
} from "./emailBuilderData";
import { EmailFormState } from "./emailMockData";
import {
  Field,
  FilterDropdown,
  UpdateEmailField,
  campaignFieldHelperClassName,
  campaignFieldLabelClassName,
  inputClassName,
  stepDescriptionClassName,
  stepTitleClassName,
  textareaClassName,
} from "./emailWizardShared";

export type StepTwoDetailsProps = {
  email: EmailFormState;
  isBusy: boolean;
  isSentRecord: boolean;
  updateEmailField: UpdateEmailField;
  updateBuilderState: UpdateEmailBuilderState;
  uploadImage: (file: File) => Promise<string>;
  footerProfile?: EmailFooterProfile | null;
  senderEmailOptions?: readonly string[];
};

const subHeadingClassName =
  "text-[16px] font-semibold leading-tight text-[#0A0A0A]";
const smallActionButtonClassName =
  "inline-flex min-h-[40px] items-center justify-center gap-2 rounded-md border border-gray-200 bg-white px-4 text-[14px] font-extrabold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60";
const smallDangerActionButtonClassName =
  "inline-flex min-h-[40px] items-center justify-center gap-2 rounded-md border border-red-300 bg-white px-4 text-[14px] font-extrabold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Color helpers for the palette picker (Step 2 ANNOTATION 1 — binding: full
// picker with gradient area, eyedropper, hue + alpha sliders, HEX and R/G/B).
// ---------------------------------------------------------------------------

type HsvaColor = { h: number; s: number; v: number; a: number };

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const parseHexColor = (
  value: string
): { r: number; g: number; b: number; a: number } | null => {
  let hex = value.trim().replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }
  if (hex.length !== 6 && hex.length !== 8) return null;
  if (!/^[0-9a-fA-F]+$/.test(hex)) return null;
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
    a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1,
  };
};

const channelToHex = (channel: number) =>
  clampNumber(Math.round(channel), 0, 255)
    .toString(16)
    .padStart(2, "0")
    .toUpperCase();

const rgbaToHexColor = (r: number, g: number, b: number, a: number) =>
  `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}${
    a >= 1 ? "" : channelToHex(a * 255)
  }`;

const rgbToHsv = (r: number, g: number, b: number) => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let h = 0;
  if (delta) {
    if (max === red) h = ((green - blue) / delta) % 6;
    else if (max === green) h = (blue - red) / delta + 2;
    else h = (red - green) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : delta / max, v: max };
};

const hsvToRgb = (h: number, s: number, v: number) => {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
};

type EyeDropperResult = { sRGBHex: string };
type EyeDropperConstructor = new () => {
  open: () => Promise<EyeDropperResult>;
};

const getEyeDropperConstructor = (): EyeDropperConstructor | null =>
  typeof window !== "undefined" && "EyeDropper" in window
    ? (window as unknown as { EyeDropper: EyeDropperConstructor }).EyeDropper
    : null;

const colorSliderCss = `
.cmp-color-slider {
  -webkit-appearance: none;
  appearance: none;
  height: 12px;
  border-radius: 9999px;
  outline: none;
}
.cmp-color-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  height: 18px;
  width: 18px;
  border-radius: 9999px;
  background: #ffffff;
  border: 1px solid #9ca3af;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.35);
  cursor: pointer;
}
.cmp-color-slider::-moz-range-thumb {
  height: 18px;
  width: 18px;
  border-radius: 9999px;
  background: #ffffff;
  border: 1px solid #9ca3af;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.35);
  cursor: pointer;
}
`;

type ColorPickerPopoverProps = {
  color: string;
  offsetX: number;
  onChange: (hex: string) => void;
};

const ColorPickerPopover = ({
  color,
  offsetX,
  onChange,
}: ColorPickerPopoverProps) => {
  const initial = parseHexColor(color) || { r: 0, g: 0, b: 0, a: 1 };
  const [hsva, setHsva] = useState<HsvaColor>(() => ({
    ...rgbToHsv(initial.r, initial.g, initial.b),
    a: initial.a,
  }));
  const [hexDraft, setHexDraft] = useState(() =>
    rgbaToHexColor(initial.r, initial.g, initial.b, initial.a)
  );

  useEffect(() => {
    const parsed = parseHexColor(color);
    if (!parsed) return;
    setHsva({ ...rgbToHsv(parsed.r, parsed.g, parsed.b), a: parsed.a });
    setHexDraft(rgbaToHexColor(parsed.r, parsed.g, parsed.b, parsed.a));
  }, [color]);

  const rgb = hsvToRgb(hsva.h, hsva.s, hsva.v);
  const roundedRgb = {
    r: Math.round(rgb.r),
    g: Math.round(rgb.g),
    b: Math.round(rgb.b),
  };
  const currentHex = rgbaToHexColor(
    roundedRgb.r,
    roundedRgb.g,
    roundedRgb.b,
    hsva.a
  );
  const eyeDropperSupported = !!getEyeDropperConstructor();

  const applyHsva = (next: HsvaColor) => {
    setHsva(next);
    const nextRgb = hsvToRgb(next.h, next.s, next.v);
    const hex = rgbaToHexColor(nextRgb.r, nextRgb.g, nextRgb.b, next.a);
    setHexDraft(hex);
    onChange(hex);
  };

  const applyRgbChannel = (channel: "r" | "g" | "b", value: number) => {
    const nextRgb = {
      ...roundedRgb,
      [channel]: clampNumber(Math.round(value) || 0, 0, 255),
    };
    setHsva({ ...rgbToHsv(nextRgb.r, nextRgb.g, nextRgb.b), a: hsva.a });
    const hex = rgbaToHexColor(nextRgb.r, nextRgb.g, nextRgb.b, hsva.a);
    setHexDraft(hex);
    onChange(hex);
  };

  const applyHexDraft = (value: string) => {
    setHexDraft(value);
    const parsed = parseHexColor(value);
    if (!parsed) return;
    setHsva({ ...rgbToHsv(parsed.r, parsed.g, parsed.b), a: parsed.a });
    onChange(rgbaToHexColor(parsed.r, parsed.g, parsed.b, parsed.a));
  };

  const pickWithEyeDropper = async () => {
    const EyeDropperCtor = getEyeDropperConstructor();
    if (!EyeDropperCtor) return;
    try {
      const result = await new EyeDropperCtor().open();
      applyHexDraft(result.sRGBHex);
    } catch {
      // Color picking was canceled.
    }
  };

  const updateSaturationFromPointer = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    applyHsva({
      ...hsva,
      s: clampNumber((event.clientX - bounds.left) / bounds.width, 0, 1),
      v: clampNumber(1 - (event.clientY - bounds.top) / bounds.height, 0, 1),
    });
  };

  return (
    <div
      className="absolute top-full z-30 mt-2 w-[260px] rounded-lg border border-[#ECECEC] bg-white p-3 shadow-xl"
      style={{ left: offsetX }}
    >
      <style>{colorSliderCss}</style>
      <div
        className="relative h-40 w-full cursor-crosshair rounded"
        role="presentation"
        style={{
          background: `linear-gradient(to top, #000000, rgba(0, 0, 0, 0)), linear-gradient(to right, #ffffff, hsl(${Math.round(
            hsva.h
          )}, 100%, 50%))`,
        }}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          updateSaturationFromPointer(event);
        }}
        onPointerMove={(event) => {
          if (event.buttons === 1) updateSaturationFromPointer(event);
        }}
      >
        <span
          className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
          style={{
            left: `${hsva.s * 100}%`,
            top: `${(1 - hsva.v) * 100}%`,
            backgroundColor: `rgb(${roundedRgb.r}, ${roundedRgb.g}, ${roundedRgb.b})`,
          }}
        />
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={pickWithEyeDropper}
          disabled={!eyeDropperSupported}
          title={
            eyeDropperSupported
              ? "Pick a colour from the screen"
              : "Your browser cannot pick a colour from the screen. Choose a colour with the colour controls instead."
          }
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-200 text-[#2F6FED] transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <ColorizeIcon fontSize="small" />
        </button>
        <div className="flex-1 space-y-2.5">
          <input
            type="range"
            min={0}
            max={360}
            step={1}
            value={Math.round(hsva.h)}
            aria-label="Hue"
            onChange={(event) =>
              applyHsva({ ...hsva, h: Number(event.target.value) })
            }
            className="cmp-color-slider w-full"
            style={{
              background:
                "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)",
            }}
          />
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={Math.round(hsva.a * 100)}
            aria-label="Opacity"
            onChange={(event) =>
              applyHsva({ ...hsva, a: Number(event.target.value) / 100 })
            }
            className="cmp-color-slider w-full"
            style={{
              background: `linear-gradient(to right, rgba(${roundedRgb.r}, ${roundedRgb.g}, ${roundedRgb.b}, 0), rgb(${roundedRgb.r}, ${roundedRgb.g}, ${roundedRgb.b})), repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 0 0 / 12px 12px`,
            }}
          />
        </div>
      </div>
      <div className="mt-3 flex items-end gap-2">
        <label className="block flex-1">
          <span className="text-[11px] font-extrabold uppercase text-gray-500">
            HEX
          </span>
          <input
            value={hexDraft}
            onChange={(event) => applyHexDraft(event.target.value)}
            onBlur={() => setHexDraft(currentHex)}
            className="mt-1 h-9 w-full rounded border border-gray-300 px-2 text-[13px] text-gray-950 outline-none focus:border-cpaGold"
          />
        </label>
        {(["r", "g", "b"] as const).map((channel) => (
          <label key={channel} className="block w-[52px]">
            <span className="text-[11px] font-extrabold uppercase text-gray-500">
              {channel.toUpperCase()}
            </span>
            <input
              type="number"
              min={0}
              max={255}
              value={roundedRgb[channel]}
              onChange={(event) =>
                applyRgbChannel(channel, Number(event.target.value))
              }
              className="mt-1 h-9 w-full rounded border border-gray-300 px-1.5 text-center text-[13px] text-gray-950 outline-none focus:border-cpaGold"
            />
          </label>
        ))}
      </div>
    </div>
  );
};

type ThemeColorSwatchProps = {
  label: string;
  color: string;
  disabled: boolean;
  onChange: (hex: string) => void;
};

const ThemeColorSwatch = ({
  label,
  color,
  disabled,
  onChange,
}: ThemeColorSwatchProps) => {
  const [open, setOpen] = useState(false);
  const [offsetX, setOffsetX] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  // Shared dropdown behavior (SH-005): close on outside click and Escape like
  // every other anchored menu in the dashboard.
  useEffect(() => {
    if (!open) {
      return;
    }
    const positionPicker = () => {
      const left = containerRef.current?.getBoundingClientRect().left || 0;
      setOffsetX(
        Math.max(12 - left, Math.min(0, window.innerWidth - left - 272))
      );
    };
    positionPicker();
    window.addEventListener("resize", positionPicker);
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("resize", positionPicker);
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div
      className="relative flex w-10 flex-col items-center gap-1.5"
      ref={containerRef}
    >
      <button
        type="button"
        onClick={() => setOpen((currentOpen) => !currentOpen)}
        disabled={disabled}
        title={label}
        aria-label={`${label} color`}
        aria-expanded={open}
        className={`flex h-10 w-10 items-center justify-center rounded-lg border-2 bg-white transition hover:border-cpaGold disabled:cursor-not-allowed disabled:opacity-60 ${open ? "border-cpaGold" : "border-[#ECECEC]"}`}
      >
        <span
          className="block h-[26px] w-[26px] rounded-[5px] border border-[#ECECEC]"
          style={{ backgroundColor: color }}
        />
      </button>
      <span className="whitespace-nowrap text-[14px] text-[#727272]">
        {label}
      </span>
      {open ? (
        <ColorPickerPopover
          color={color}
          offsetX={offsetX}
          onChange={onChange}
        />
      ) : null}
    </div>
  );
};

// Footer appearance is intentionally immutable in Step 2. Its stored theme
// value remains in builder state so existing drafts continue to render exactly
// as saved, but only content-theme colors are exposed here.
const themeSwatches: Array<{
  field: keyof EmailBuilderState["theme"];
  label: string;
}> = [
  { field: "highlightColor", label: "Primary" },
  { field: "accentColor", label: "Accent" },
  { field: "textColor", label: "Text" },
  { field: "backgroundColor", label: "Background" },
];

const StepTwoDetails = ({
  email,
  isBusy,
  isSentRecord,
  updateEmailField,
  updateBuilderState,
  uploadImage,
  footerProfile,
  senderEmailOptions = [],
}: StepTwoDetailsProps) => {
  const noun = useCmpOrganizationNoun();
  const [logoError, setLogoError] = useState("");
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const isLocked = isBusy || isSentRecord;
  const isBuilderLocked = isLocked || !!email.builderState.preservedDocument;

  const handleLogoFile = async (file?: File | null) => {
    if (!file) return;
    const fileTypeError = getEmailImageFileError({ file });
    if (fileTypeError) {
      setLogoError(fileTypeError);
      return;
    }
    setLogoError("");
    setIsUploadingLogo(true);
    try {
      const logoUrl = await uploadImage(file);
      updateBuilderState((currentState) => ({ ...currentState, logoUrl }));
    } catch (error) {
      setLogoError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback(
            "upload the logo",
            "Check your internet connection."
          )
        )
      );
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    setLogoError("");
    updateBuilderState((currentState) => ({ ...currentState, logoUrl: "" }));
  };

  return (
    <div className="space-y-[22px]">
      <div>
        <h2 className={stepTitleClassName}>Campaign Details</h2>
        <p className={`${stepDescriptionClassName} !mt-[6px]`}>
          Provide the specific campaign details for this communication,
          including the campaign name, subject line, sender information, and
          preview text. These details help improve email deliverability,
          increase open rates, and create a more professional experience for
          recipients.
        </p>
      </div>
      {/* B10 (POL-002 Emails leg): shared SectionShell card with the
          transcription-bound numbered gold badge (variant="numbered"). */}
      <SectionShell
        id="email-step2-basic-information"
        variant="numbered"
        number={1}
        title="Basic Information"
        description="Give your campaign a name and add a short description for internal use only"
      >
        <div className="space-y-[22px]">
          <Field
            appearance="campaignDetails"
            label="Campaign Name"
            helper="For internal use. Name for this campaign (not visible to recipients)."
            required
          >
            <input
              value={email.campaignName}
              onChange={(event) =>
                updateEmailField("campaignName", event.target.value)
              }
              className={inputClassName}
              disabled={isLocked}
              placeholder={`e.g., June ${noun.Singular} Newsletter`}
            />
          </Field>
          <Field
            appearance="campaignDetails"
            label={
              <>
                Campaign Description{" "}
                <span className="text-[#9E9E9E]">(Optional)</span>
              </>
            }
            helper="Add specific details for internal reference only."
          >
            <textarea
              value={email.campaignDescription}
              onChange={(event) =>
                updateEmailField("campaignDescription", event.target.value)
              }
              className={`${textareaClassName} !min-h-[88px]`}
              disabled={isLocked}
              placeholder={`e.g., Monthly newsletter featuring upcoming events, member highlights, and ${noun.singular} updates.`}
            />
          </Field>
        </div>
      </SectionShell>
      <SectionShell
        id="email-step2-email-settings"
        variant="numbered"
        number={2}
        title="Email Settings"
        description="Define the key email details your recipients will see."
      >
        <div className="grid grid-cols-1 gap-x-5 gap-y-[22px] lg:grid-cols-2">
          <div className="lg:col-span-2">
            <Field
              appearance="campaignDetails"
              label="Email Subject Line"
              helper="Keep it clear, compelling and under 60 characters for the best open rates."
              required
            >
              <input
                value={email.subject}
                onChange={(event) =>
                  updateEmailField("subject", event.target.value.slice(0, 60))
                }
                maxLength={60}
                className={inputClassName}
                disabled={isLocked}
                placeholder="Enter Subject"
              />
              <p className="mt-2 text-[12px] text-[#0A0A0A]">
                {email.subject.length}/60 characters
              </p>
            </Field>
          </div>
          <div className="lg:col-span-2">
            <Field
              appearance="campaignDetails"
              label="Preview Text (Preheader)"
              helper={
                <>
                  This appears after the subject line in most email inboxes.{" "}
                  <strong>
                    The pre-header is the first things recipients see before
                    opening an email, make it compelling to encourage reader to
                    open.
                  </strong>
                </>
              }
            >
              <input
                value={email.preheader}
                onChange={(event) =>
                  updateEmailField(
                    "preheader",
                    event.target.value.slice(0, 100)
                  )
                }
                maxLength={100}
                className={inputClassName}
                disabled={isLocked}
                placeholder="Enter Preheader Details"
              />
              <p className="mt-2 text-[12px] text-[#0A0A0A]">
                {email.preheader.length}/100 characters
              </p>
            </Field>
          </div>
          <Field
            appearance="campaignDetails"
            label="From Name"
            required
            helper="The name recipients will see in their inbox."
          >
            <input
              value={email.fromName}
              onChange={(event) =>
                updateEmailField("fromName", event.target.value)
              }
              className={inputClassName}
              disabled={isLocked}
              placeholder={`Enter ${noun.Singular} or Contact Name`}
            />
          </Field>
          <Field
            appearance="campaignDetails"
            label="From Email Address"
            required
            helper="The email recipients will see in their inbox."
          >
            <input
              type="email"
              value={email.fromEmail}
              onChange={(event) => updateEmailField("fromEmail", event.target.value)}
              className={inputClassName}
              disabled={isLocked}
              aria-label="From Email Address"
              aria-describedby={!isSentRecord && !senderEmailOptions.includes(email.fromEmail.trim().toLowerCase()) ? "email-sender-readiness" : undefined}
              placeholder={`Enter ${noun.Singular} or Contact Email Address`}
            />
            {!isSentRecord && !senderEmailOptions.includes(email.fromEmail.trim().toLowerCase()) && (
              <p id="email-sender-readiness" role="status" className="mt-2 text-[13px] leading-5 text-[#727272]">
                {senderEmailOptions.length === 0
                  ? `No sender email is set up for your ${noun.singular}, so this email cannot be sent. Add a sender email in ${noun.Singular} Set Up, then try again.`
                  : `${email.fromEmail.trim() || "This address"} is not set up as a sender email. Enter a sender email that is set up in ${noun.Singular} Set Up, or add this address there first.`}
              </p>
            )}
            {email.senderReadinessDetail ? (
              <p className="mt-2 text-[13px] leading-5 text-gray-500">
                {email.senderReadinessDetail}
              </p>
            ) : null}
          </Field>
          <div className="lg:col-span-2">
            <Field
              appearance="campaignDetails"
              label={
                <>
                  Reply-To Email{" "}
                  <span className="text-[#9E9E9E]">(Optional)</span>
                </>
              }
              helper="Replies to this email will go to this address if different then sent from email address"
            >
              <input
                value={email.replyToEmail}
                onChange={(event) =>
                  updateEmailField("replyToEmail", event.target.value)
                }
                className={inputClassName}
                disabled={isLocked}
                placeholder="Enter Alternative Reply-To Email"
              />
            </Field>
          </div>
        </div>
      </SectionShell>
      <SectionShell
        id="email-step2-branding-personalization"
        variant="numbered"
        number={3}
        title="Branding & Personalization"
        description="Maintain a consistent brand experience and connect with your audience."
      >
        <div className="space-y-[22px]">
          <div>
            <span className={campaignFieldLabelClassName}>{noun.Singular} Logo</span>
            <span className={campaignFieldHelperClassName}>
              Ideally a square PNG or JPG image 5MB in size
            </span>
            <div className="mt-2">
              <input
                ref={logoInputRef}
                type="file"
                accept={emailImageAccept}
                className="hidden"
                disabled={isBuilderLocked || isUploadingLogo}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  void handleLogoFile(file);
                }}
              />
              {email.builderState.logoUrl ? (
                <div className="max-w-[620px] rounded-lg border border-[#9E9E9E] bg-white px-6 py-5">
                  <div className="flex flex-col items-center gap-4">
                    <img
                      src={email.builderState.logoUrl}
                      alt={`${noun.Singular} logo preview`}
                      className="max-h-24 max-w-[220px] object-contain"
                    />
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <button
                        type="button"
                        className={smallActionButtonClassName}
                        onClick={() => logoInputRef.current?.click()}
                        disabled={isBuilderLocked || isUploadingLogo}
                      >
                        {isUploadingLogo ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        Replace Logo
                      </button>
                      <button
                        type="button"
                        className={smallDangerActionButtonClassName}
                        onClick={removeLogo}
                        disabled={isBuilderLocked || isUploadingLogo}
                      >
                        Delete Logo
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={isBuilderLocked || isUploadingLogo}
                  className="flex min-h-[123px] w-full max-w-[620px] flex-col items-center justify-center gap-2.5 rounded-lg border-[1.5px] border-dashed border-[#9E9E9E] bg-white p-[34px] text-[#0A0A0A] transition hover:border-cpaGold disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isUploadingLogo ? (
                    <>
                      <CircularProgress color="inherit" size={26} />
                      <span className="text-[15px]">Uploading logo...</span>
                    </>
                  ) : (
                    <>
                      <FileUploadOutlinedIcon
                        className="text-gray-400"
                        sx={{ fontSize: 22 }}
                      />
                      <span className="text-[15px]">Click Upload Logo</span>
                    </>
                  )}
                </button>
              )}
              {logoError ? (
                <p className="mt-2 text-sm text-red-600">{logoError}</p>
              ) : null}
            </div>
          </div>
          <div>
            <h3 className={subHeadingClassName}>Default Content Theme</h3>
            <div className="mt-[14px]">
              <span className="text-[14px] font-semibold text-[#0A0A0A]">
                Colour Palette
              </span>
              <span className={campaignFieldHelperClassName}>
                This colour palette will be reflected in the email templates, you
                can click on the colour to edit them. The text colour will
                automatically update to black or white, based on the background
                colour selected.
              </span>
              <div className="mt-3 flex flex-wrap gap-[26px]">
                {themeSwatches.map((swatch) => (
                  <ThemeColorSwatch
                    key={swatch.field}
                    label={swatch.label}
                    color={email.builderState.theme[swatch.field] || "#9DCBF7"}
                    disabled={isBuilderLocked}
                    onChange={(hex) =>
                      updateBuilderState((currentState) => ({
                        ...currentState,
                        theme: {
                          ...currentState.theme,
                          [swatch.field]: hex,
                          ...(swatch.field === "backgroundColor"
                            ? { textColor: getReadableEmailTextColor(hex) }
                            : {}),
                        },
                      }))
                    }
                  />
                ))}
              </div>
            </div>
          </div>
          <div>
            <h3 className={subHeadingClassName}>Personalization</h3>
            <Field label="Greeting Line">
              <div className="grid gap-5 md:grid-cols-2">
                <FilterDropdown label="Greeting Line" placeholder="Select Greeting Line" compact singleSelect
                  options={[{ id: "firstName", label: "First Name" }, { id: "fullName", label: "First Name and Last Name" }, { id: "companyName", label: "Company Name" }, { id: "none", label: "Leave Blank" }]}
                  values={email.builderState.greetingLine ? [email.builderState.greetingLine] : []} disabled={isBuilderLocked}
                  onChange={(values) => {
                    const greetingLine = values[0];
                    if (greetingLine === "none" || greetingLine === "firstName" || greetingLine === "fullName" || greetingLine === "companyName") updateBuilderState(current => ({ ...current, greetingLine }));
                  }} />
                <div className="rounded-lg border border-[#ECECEC] bg-[#F0F0F0] px-[14px] py-[11px] text-[14px]">
                  Preview: {getEmailGreetingText(email.builderState.greetingLine) || "No greeting"}
                </div>
              </div>
            </Field>
          </div>
          {/* D10 / design (16) Step 2 has no footer section; the footer
              message is edited in the Content Builder Footer block. */}
        </div>
      </SectionShell>
    </div>
  );
};

export default StepTwoDetails;
